import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState, type ReactElement, type ReactNode } from "react";
import {
  CustomFieldsPanel,
  Field,
  focusFirstInvalid,
  FormPage,
  FormSection,
  ImageGallery,
  MoneyInput,
  PercentInput,
  QtyInput,
  QueryStatus,
  ScaledInput,
  Tabs,
  useToast,
} from "@cx/ui";
import {
  MAX_PRODUCT_IMAGES,
  SPED_ITEM_TYPES,
  type CustomFieldRow,
  type ProductDetail,
  type ProductFormat,
  type ProductFormValues,
  type ProductImageView,
  type ProductInput,
} from "@cx/contracts";
import { formatLength, formatWeightKg, lengthToMm, weightKgToGrams, type LengthUnit } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";
import { ComponentsTab } from "./ComponentsTab";
import { StructureTab } from "./StructureTab";
import { SupplierPicker } from "./SupplierPicker";
import { VariationsTab } from "./VariationsTab";
import { StockPanel } from "../stock/StockPanel";
import {
  EMPTY,
  componentInfo,
  supplierNames,
  toFormValues,
  useProductForm,
  type ComponentInfo,
  type SupplierValues,
} from "./useProductForm";

/** Origem da mercadoria (tabela A do CST do ICMS). */
const ORIGINS = [
  "0 - Nacional",
  "1 - Estrangeira, importação direta",
  "2 - Estrangeira, adquirida no mercado interno",
  "3 - Nacional, conteúdo de importação acima de 40% e até 70%",
  "4 - Nacional, produção conforme processos produtivos básicos",
  "5 - Nacional, conteúdo de importação até 40%",
  "6 - Estrangeira, importação direta, sem similar nacional (CAMEX)",
  "7 - Estrangeira, mercado interno, sem similar nacional (CAMEX)",
  "8 - Nacional, conteúdo de importação acima de 70%",
];
const SPED_LABEL: Record<(typeof SPED_ITEM_TYPES)[number], string> = {
  "00": "Mercadoria para revenda",
  "01": "Matéria-prima",
  "02": "Embalagem",
  "03": "Produto em processo",
  "04": "Produto acabado",
  "05": "Subproduto",
  "06": "Produto intermediário",
  "07": "Material de uso e consumo",
  "08": "Ativo imobilizado",
  "09": "Serviços",
  "10": "Outros insumos",
  "99": "Outras",
};
const UNIT_LABEL: Record<LengthUnit, string> = { m: "Metros", cm: "Centímetros", mm: "Milímetros" };
const FORMAT_LABEL: Record<ProductFormat, string> = {
  S: "Simples",
  V: "Com variações",
  E: "Com composição (kit)",
};
/** Erros do servidor que dizem respeito ao formato (mostrados no campo Formato). */
const FORMAT_ERRORS = new Set(["invalid_format", "has_variations", "component_in_use"]);

/** Inteiro de um input numérico (dias, volumes): vazio vira null; sem float. */
const intOrNull = (s: string) => (/^\d+$/.test(s) ? Number.parseInt(s, 10) : null);

/**
 * Rota do formulário. Espera o detalhe buscado NESTA montagem (nunca semeia com cache velho) e remonta o formulário
 * a cada id, como o de contatos (FE16).
 */
export function ProductFormPage(): ReactElement {
  const { id } = useParams({ strict: false });
  const { companyId, api } = useSession();
  const detail = useQuery({
    queryKey: ["product", companyId, id],
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<ProductDetail>(`/products/${id}`),
  });
  if (id && detail.isError && !detail.data) return <QueryStatus error={detail.error} />;
  if (id && !detail.isFetchedAfterMount) return <QueryStatus loading />;
  return (
    <ProductForm
      key={id ?? "novo"}
      id={id}
      detail={id ? detail.data : undefined}
      initial={id && detail.data ? toFormValues(detail.data) : EMPTY}
      names={id && detail.data ? supplierNames(detail.data) : {}}
      components={id && detail.data ? componentInfo(detail.data) : {}}
    />
  );
}

function Check({
  label,
  checked,
  onChange,
  className = "col-md-4",
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
  className?: string;
}) {
  const [id] = useState(() => `chk-${Math.random().toString(36).slice(2)}`);
  return (
    <div className={`${className} mb-3 d-flex align-items-end`}>
      <div className="form-check">
        <input
          id={id}
          type="checkbox"
          className="form-check-input"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label className="form-check-label" htmlFor={id}>
          {label}
        </label>
      </div>
    </div>
  );
}

function ProductForm({
  id,
  detail,
  initial,
  names: initialNames,
  components: initialComponents,
}: {
  id?: string;
  /** Detalhe atual (atualiza depois de gerar a grade, sem remontar o formulário). */
  detail?: ProductDetail;
  initial: ProductFormValues;
  names: Record<string, string>;
  components: Record<string, ComponentInfo>;
}) {
  const isNew = !id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const f = useProductForm(initial);
  const v = f.values;
  const tax = v.tax ?? {};
  const err = f.errors;
  const [names, setNames] = useState(initialNames);
  const [compInfo, setCompInfo] = useState(initialComponents);
  const isChild = !!detail?.parentId;
  /** Filho com "Utilizar informações do produto pai": tributação, dimensões e descrição vêm do pai (só leitura). */
  const inherited = isChild && v.inheritParent !== false;
  const format: ProductFormat = v.format ?? "S";
  const unit: LengthUnit = v.dimensionUnit ?? "cm";
  const canEdit = can(perms, "products", isNew ? "create" : "update");
  // Sem products:read_cost a API não manda custo e ignora o que vier: os campos de custo nem aparecem.
  const canSeeCost = can(perms, "products", "read_cost");

  const fields = useQuery({
    queryKey: ["custom-fields", companyId, "products"],
    queryFn: () => api<{ data: CustomFieldRow[] }>("/custom-fields?module=products"),
  });
  const imagesKey = ["product-images", companyId, id];
  const images = useQuery({
    queryKey: imagesKey,
    enabled: !isNew,
    queryFn: () => api<{ data: ProductImageView[] }>(`/products/${id}/images`),
  });

  const save = useMutation({
    mutationFn: (payload: ProductInput) =>
      api<{ id?: string; warnings: string[] }>(isNew ? "/products" : `/products/${id}`, {
        method: isNew ? "POST" : "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: (r) => {
      toast("success", "Produto salvo");
      if (r.warnings.includes("gtin_duplicate")) toast("warning", "Já existe outro produto com este GTIN.");
      f.markSaved();
      const savedId = r.id ?? id;
      qc.invalidateQueries({ queryKey: ["products", companyId] });
      if (savedId) qc.invalidateQueries({ queryKey: ["product", companyId, savedId] });
      // O servidor troca os campos herdados pelos do pai: o formulário passa a mostrar o que foi gravado.
      if (inherited && id)
        void qc
          .fetchQuery({
            queryKey: ["product", companyId, id],
            queryFn: () => api<ProductDetail>(`/products/${id}`),
          })
          .then((d) => f.reset(toFormValues(d)));
      if (isNew && r.id) nav({ to: "/cadastros/produtos/$id", params: { id: r.id }, replace: true });
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) f.setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_sku") f.setErrors({ sku: e.message });
      else if (e instanceof ApiFailure && e.code === "invalid_supplier")
        f.setErrors({ suppliers: e.message });
      else if (e instanceof ApiFailure && e.code === "invalid_component")
        f.setErrors({ components: e.message });
      else if (e instanceof ApiFailure && FORMAT_ERRORS.has(e.code)) f.setErrors({ format: e.message });
      toast("danger", errorMessage(e));
      focusFirstInvalid();
    },
  });
  const onSave = () => {
    const p = f.validate();
    if (p) return save.mutate(p);
    toast("danger", "Verifique os campos destacados.");
    focusFirstInvalid();
  };

  const imageChanged = {
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: imagesKey });
      qc.invalidateQueries({ queryKey: ["products", companyId] });
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  };
  const upload = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api(`/products/${id}/images`, { method: "POST", body: fd });
    },
    ...imageChanged,
  });
  const addUrl = useMutation({
    mutationFn: (url: string) =>
      api(`/products/${id}/images/url`, { method: "POST", body: JSON.stringify({ url }) }),
    ...imageChanged,
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      api(`/products/${id}/images/order`, { method: "PUT", body: JSON.stringify({ ids }) }),
    ...imageChanged,
  });
  const delImage = useMutation({
    mutationFn: (imageId: string) => api(`/products/${id}/images/${imageId}`, { method: "DELETE" }),
    ...imageChanged,
  });
  const imagesBusy = upload.isPending || addUrl.isPending || reorder.isPending || delImage.isPending;

  const suppliers = v.suppliers ?? [];
  const setSupplier = (i: number, patch: Partial<SupplierValues>) =>
    f.set(
      "suppliers",
      suppliers.map((s, j) =>
        j === i ? { ...s, ...patch } : patch.isDefault ? { ...s, isDefault: false } : s,
      ),
    );

  const text = (
    label: string,
    key: keyof ProductFormValues,
    cls: string,
    extra: Partial<{ max: number }> = {},
  ) => (
    <Field label={label} error={err[key]} className={cls}>
      <input
        value={(v[key] as string | null | undefined) ?? ""}
        maxLength={extra.max}
        onChange={(e) => f.set(key, e.target.value as never)}
      />
    </Field>
  );
  const taxText = (label: string, key: keyof typeof tax, cls: string, hint?: string): ReactNode => (
    <Field label={label} error={err[`tax.${key}`]} hint={hint} className={cls}>
      <input
        value={(tax[key] as string | null | undefined) ?? ""}
        onChange={(e) => f.setTax({ [key]: e.target.value })}
      />
    </Field>
  );
  const taxMoney = (label: string, key: keyof typeof tax, cls = "col-md-4"): ReactNode => (
    <Field label={label} error={err[`tax.${key}`]} className={cls}>
      <MoneyInput
        value={(tax[key] as number | null | undefined) ?? null}
        onChange={(c) => f.setTax({ [key]: c })}
      />
    </Field>
  );
  const length = (label: string, key: "widthMm" | "heightMm" | "depthMm") => (
    <Field label={label} error={err[key]} className="col-md-3">
      <ScaledInput
        key={unit}
        value={v[key] ?? null}
        parse={(t) => lengthToMm(t, unit)}
        format={(mm) => formatLength(mm, unit)}
        suffix={unit}
        onChange={(mm) => f.set(key, mm)}
      />
    </Field>
  );
  const int = (label: string, key: "volumes" | "itemsPerBox" | "crossdockingDays", cls = "col-md-3") => (
    <Field label={label} error={err[key]} className={cls}>
      <input
        type="number"
        min={0}
        step={1}
        inputMode="numeric"
        value={v[key] ?? ""}
        onChange={(e) => f.set(key, intOrNull(e.target.value))}
      />
    </Field>
  );

  return (
    <FormPage
      title={isNew ? "Novo produto" : v.name || "Produto"}
      breadcrumb={["Cadastros", "Produtos", isNew ? "Incluir" : "Editar"]}
      onSave={onSave}
      onCancel={() => nav({ to: "/cadastros/produtos" })}
      saving={save.isPending}
      dirty={f.dirty}
    >
      {isChild && detail?.parent && (
        <div className="alert alert-info d-flex flex-wrap align-items-center gap-3" role="note">
          <span className="flex-grow-1">
            Variação de{" "}
            <Link to="/cadastros/produtos/$id" params={{ id: detail.parent.id }} className="alert-link">
              {detail.parent.name}
            </Link>
            {detail.variationValues.length > 0 && (
              <> · {detail.variationValues.map((x) => `${x.attribute}: ${x.value}`).join(" · ")}</>
            )}
          </span>
          <Check
            label="Utilizar informações do produto pai"
            className="ms-auto"
            checked={v.inheritParent !== false}
            onChange={(b) => f.set("inheritParent", b)}
          />
        </div>
      )}
      <Tabs
        tabs={[
          {
            key: "caracteristicas",
            label: "Características",
            content: (
              <>
                <FormSection title="Dados gerais">
                  <Field label="Nome" required error={err.name} className="col-md-8">
                    <input value={v.name} maxLength={120} onChange={(e) => f.set("name", e.target.value)} />
                  </Field>
                  {text("SKU", "sku", "col-md-4", { max: 60 })}
                  {!isChild && (
                    <Field label="Formato" error={err.format} className="col-md-4">
                      <select
                        className="form-select"
                        value={format}
                        onChange={(e) => {
                          const next = e.target.value as ProductFormat;
                          // Só kit tem componentes: sair do kit limpa a lista (a aba some e o erro ficaria escondido).
                          f.setValues((x) => ({
                            ...x,
                            format: next,
                            components: next === "E" ? x.components : [],
                          }));
                        }}
                      >
                        {(Object.keys(FORMAT_LABEL) as ProductFormat[]).map((k) => (
                          <option key={k} value={k}>
                            {FORMAT_LABEL[k]}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                  <Field label="Tipo" className="col-md-3">
                    <select
                      className="form-select"
                      value={v.kind}
                      onChange={(e) => f.set("kind", e.target.value as "P" | "S")}
                    >
                      <option value="P">Produto</option>
                      <option value="S">Serviço</option>
                    </select>
                  </Field>
                  <Field label="Situação" className="col-md-3">
                    <select
                      className="form-select"
                      value={v.status}
                      onChange={(e) => f.set("status", e.target.value as "active" | "inactive")}
                    >
                      <option value="active">Ativo</option>
                      <option value="inactive">Inativo</option>
                    </select>
                  </Field>
                  <Field label="Unidade" error={err.unit} className="col-md-2">
                    <input
                      value={v.unit ?? ""}
                      maxLength={6}
                      onChange={(e) => f.set("unit", e.target.value)}
                    />
                  </Field>
                  <Field label="Condição" className="col-md-4">
                    <select
                      className="form-select"
                      value={v.condition}
                      onChange={(e) => f.set("condition", e.target.value as ProductFormValues["condition"])}
                    >
                      <option value="unspecified">Não especificado</option>
                      <option value="new">Novo</option>
                      <option value="used">Usado</option>
                      <option value="refurbished">Recondicionado</option>
                    </select>
                  </Field>
                  <Field label="Preço de venda" error={err.priceCents} className="col-md-4">
                    <MoneyInput value={v.priceCents ?? null} onChange={(c) => f.set("priceCents", c)} />
                  </Field>
                  {canSeeCost && (
                    <Field label="Preço de custo" error={err.costCents} className="col-md-4">
                      <MoneyInput value={v.costCents ?? null} onChange={(c) => f.set("costCents", c)} />
                    </Field>
                  )}
                  <Field label="Produção" className="col-md-4">
                    <select
                      className="form-select"
                      value={v.production}
                      onChange={(e) => f.set("production", e.target.value as "own" | "third_party")}
                    >
                      <option value="own">Própria</option>
                      <option value="third_party">Terceiros</option>
                    </select>
                  </Field>
                  {text("GTIN/EAN", "gtin", "col-md-4")}
                  {text("GTIN/EAN tributário", "gtinTax", "col-md-4")}
                  {text("Linha", "productLine", "col-md-4", { max: 60 })}
                  <Field label="Validade" error={err.expirationDate} className="col-md-4">
                    <input
                      type="date"
                      value={v.expirationDate ?? ""}
                      onChange={(e) => f.set("expirationDate", e.target.value || null)}
                    />
                  </Field>
                  <Check
                    label="Frete grátis"
                    checked={!!v.freeShipping}
                    onChange={(b) => f.set("freeShipping", b)}
                  />
                </FormSection>
                <FormSection title="Dimensões e peso" defaultOpen={false} disabled={inherited}>
                  <Field label="Peso líquido" error={err.netWeightG} className="col-md-3">
                    <ScaledInput
                      value={v.netWeightG ?? null}
                      parse={weightKgToGrams}
                      format={formatWeightKg}
                      suffix="kg"
                      onChange={(g) => f.set("netWeightG", g)}
                    />
                  </Field>
                  <Field label="Peso bruto" error={err.grossWeightG} className="col-md-3">
                    <ScaledInput
                      value={v.grossWeightG ?? null}
                      parse={weightKgToGrams}
                      format={formatWeightKg}
                      suffix="kg"
                      onChange={(g) => f.set("grossWeightG", g)}
                    />
                  </Field>
                  <Field label="Unidade das medidas" className="col-md-3">
                    <select
                      className="form-select"
                      value={unit}
                      onChange={(e) => f.set("dimensionUnit", e.target.value as LengthUnit)}
                    >
                      {(Object.keys(UNIT_LABEL) as LengthUnit[]).map((u) => (
                        <option key={u} value={u}>
                          {UNIT_LABEL[u]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {int("Volumes", "volumes")}
                  {length("Largura", "widthMm")}
                  {length("Altura", "heightMm")}
                  {length("Profundidade", "depthMm")}
                  {int("Itens por caixa", "itemsPerBox")}
                </FormSection>
                <FormSection title="Descrição e links" defaultOpen={false} disabled={inherited}>
                  {text("Descrição curta", "shortDescription", "col-12", { max: 255 })}
                  <Field label="Descrição complementar" error={err.longDescription} className="col-12">
                    <textarea
                      rows={5}
                      value={v.longDescription ?? ""}
                      onChange={(e) => f.set("longDescription", e.target.value)}
                    />
                  </Field>
                  <Field label="Link externo" error={err.externalLink} className="col-md-6">
                    <input
                      type="url"
                      value={v.externalLink ?? ""}
                      onChange={(e) => f.set("externalLink", e.target.value)}
                    />
                  </Field>
                  <Field label="Vídeo" error={err.videoUrl} className="col-md-6">
                    <input
                      type="url"
                      value={v.videoUrl ?? ""}
                      onChange={(e) => f.set("videoUrl", e.target.value)}
                    />
                  </Field>
                  <Field label="Observações" error={err.notes} className="col-12">
                    <textarea
                      rows={3}
                      value={v.notes ?? ""}
                      onChange={(e) => f.set("notes", e.target.value)}
                    />
                  </Field>
                </FormSection>
              </>
            ),
          },
          {
            key: "tributacao",
            label: "Tributação",
            content: (
              <>
                {inherited && (
                  <p className="text-muted small">A tributação segue o produto pai desta variação.</p>
                )}
                <FormSection title="Classificação fiscal" disabled={inherited}>
                  <Field label="Origem" error={err["tax.origin"]} className="col-md-6">
                    <select
                      className="form-select"
                      value={tax.origin ?? 0}
                      onChange={(e) => f.setTax({ origin: Number(e.target.value) })}
                    >
                      {ORIGINS.map((o, i) => (
                        <option key={o} value={i}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {taxText("NCM", "ncm", "col-md-3", "8 dígitos, ex.: 6109.10.00")}
                  {taxText("CEST", "cest", "col-md-3")}
                  <Field label="Tipo do item (SPED)" error={err["tax.spedItemType"]} className="col-md-6">
                    <select
                      className="form-select"
                      value={tax.spedItemType ?? ""}
                      onChange={(e) =>
                        f.setTax({
                          spedItemType: (e.target.value || null) as (typeof SPED_ITEM_TYPES)[number] | null,
                        })
                      }
                    >
                      <option value="">Não informado</option>
                      {SPED_ITEM_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t} - {SPED_LABEL[t]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {taxText("EX TIPI", "exTipi", "col-md-3")}
                  {taxText("Nº FCI", "fciNumber", "col-md-3")}
                  {taxText("Código LC 116 (serviço)", "serviceListCode", "col-md-4", "Ex.: 01.07")}
                  {taxText("Classificação IBS/CBS (cClassTrib)", "ibsCbsClass", "col-md-4")}
                  <Field label="Tributos aproximados" error={err["tax.approxTaxPct4"]} className="col-md-4">
                    <PercentInput
                      value={tax.approxTaxPct4 ?? null}
                      onChange={(p) => f.setTax({ approxTaxPct4: p })}
                    />
                  </Field>
                </FormSection>
                <FormSection title="IPI, PIS e COFINS" defaultOpen={false} disabled={inherited}>
                  {taxText("Classe de enquadramento do IPI", "ipiClass", "col-md-4")}
                  {taxText("Código do selo", "ipiSealCode", "col-md-4")}
                  <div className="col-md-4" />
                  {taxMoney("IPI fixo", "ipiFixedCents")}
                  {taxMoney("PIS fixo", "pisFixedCents")}
                  {taxMoney("COFINS fixo", "cofinsFixedCents")}
                </FormSection>
                <FormSection title="ICMS ST retido" defaultOpen={false} disabled={inherited}>
                  {taxMoney("Base do ICMS ST retido", "icmsStBaseRetCents")}
                  {taxMoney("Valor do ICMS ST retido", "icmsStRetCents")}
                  {taxMoney("ICMS próprio do substituto", "icmsOwnSubstituteCents")}
                </FormSection>
                <FormSection title="Informações adicionais" defaultOpen={false} disabled={inherited}>
                  <Field label="Informações adicionais" error={err["tax.extraInfo"]} className="col-12">
                    <textarea
                      rows={3}
                      maxLength={500}
                      value={tax.extraInfo ?? ""}
                      onChange={(e) => f.setTax({ extraInfo: e.target.value })}
                    />
                  </Field>
                </FormSection>
              </>
            ),
          },
          ...(format === "V" && !isChild
            ? [
                {
                  key: "variacoes",
                  label: "Variações",
                  content:
                    isNew || !detail ? (
                      <p className="text-muted">Salve o produto para gerar a grade de variações.</p>
                    ) : detail.format !== "V" ? (
                      <p className="text-muted">
                        Salve o produto com o formato “Com variações” para gerar a grade.
                      </p>
                    ) : (
                      <VariationsTab
                        detail={detail}
                        dirty={f.dirty}
                        canGenerate={can(perms, "products", "create") && can(perms, "products", "update")}
                      />
                    ),
                },
              ]
            : []),
          ...(format === "E"
            ? [
                {
                  key: "composicao",
                  label: "Composição",
                  content: (
                    <ComponentsTab
                      selfId={id}
                      components={v.components ?? []}
                      info={compInfo}
                      kit={detail?.format === "E" ? detail.kit : undefined}
                      error={err.components}
                      canEdit={canEdit}
                      onChange={(c) => f.set("components", c)}
                      onInfo={(cid, i) => setCompInfo((m) => ({ ...m, [cid]: i }))}
                    />
                  ),
                },
              ]
            : []),
          ...(format === "S" && id && detail?.format === "S"
            ? [
                {
                  key: "estrutura",
                  label: "Estrutura",
                  content: <StructureTab productId={id} canEdit={canEdit} />,
                },
              ]
            : []),
          {
            key: "estoque",
            label: "Estoque",
            content: (
              <>
                <FormSection title="Parâmetros de estoque">
                  <Field label="Estoque mínimo" error={err.stockMinQ4} className="col-md-3">
                    <QtyInput value={v.stockMinQ4 ?? null} onChange={(q) => f.set("stockMinQ4", q)} />
                  </Field>
                  <Field label="Estoque máximo" error={err.stockMaxQ4} className="col-md-3">
                    <QtyInput value={v.stockMaxQ4 ?? null} onChange={(q) => f.set("stockMaxQ4", q)} />
                  </Field>
                  {int("Crossdocking (dias)", "crossdockingDays")}
                  {text("Localização", "location", "col-md-3", { max: 60 })}
                  <Check
                    label="Controlar estoque"
                    checked={v.stockControl ?? true}
                    onChange={(b) => f.set("stockControl", b)}
                  />
                  <Check
                    label="Controle de lote"
                    checked={!!v.batchControl}
                    onChange={(b) => f.set("batchControl", b)}
                  />
                </FormSection>
                {!isNew && id && can(perms, "stock", "read") && (
                  <FormSection title="Saldos e movimentos">
                    <div className="col-12">
                      {f.dirty && (
                        <p className="text-muted small">
                          Salve o produto para que mudanças de formato ou de controle de estoque valham aqui.
                        </p>
                      )}
                      <StockPanel productId={id} />
                    </div>
                  </FormSection>
                )}
              </>
            ),
          },
          {
            key: "fornecedores",
            label: "Fornecedores",
            content: (
              <div>
                {err.suppliers && (
                  <div className="alert alert-danger py-2" role="alert">
                    {err.suppliers}
                  </div>
                )}
                {suppliers.length === 0 ? (
                  <p className="text-muted">Nenhum fornecedor.</p>
                ) : (
                  <div className="table-responsive mb-3">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th scope="col">Fornecedor</th>
                          <th scope="col">Código no fornecedor</th>
                          <th scope="col">Descrição no fornecedor</th>
                          {canSeeCost && <th scope="col">Custo</th>}
                          <th scope="col">Padrão</th>
                          <th scope="col">
                            <span className="visually-hidden">Ações</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {suppliers.map((s, i) => {
                          const name = names[s.supplierId] ?? s.supplierId;
                          return (
                            <tr key={s.supplierId}>
                              <td>{name}</td>
                              <td>
                                <input
                                  className="form-control form-control-sm"
                                  aria-label={`Código no fornecedor ${name}`}
                                  maxLength={60}
                                  value={s.supplierCode ?? ""}
                                  onChange={(e) => setSupplier(i, { supplierCode: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  className="form-control form-control-sm"
                                  aria-label={`Descrição no fornecedor ${name}`}
                                  maxLength={120}
                                  value={s.supplierDescription ?? ""}
                                  onChange={(e) => setSupplier(i, { supplierDescription: e.target.value })}
                                />
                              </td>
                              {canSeeCost && (
                                <td style={{ minWidth: 160 }}>
                                  <MoneyInput
                                    aria-label={`Custo no fornecedor ${name}`}
                                    className="form-control form-control-sm text-end"
                                    value={s.lastCostCents ?? null}
                                    onChange={(c) => setSupplier(i, { lastCostCents: c })}
                                  />
                                </td>
                              )}
                              <td>
                                <input
                                  type="radio"
                                  name="default-supplier"
                                  className="form-check-input"
                                  aria-label={`Fornecedor padrão: ${name}`}
                                  checked={!!s.isDefault}
                                  onChange={() => setSupplier(i, { isDefault: true })}
                                />
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  aria-label={`Remover fornecedor ${name}`}
                                  onClick={() =>
                                    f.set(
                                      "suppliers",
                                      suppliers.filter((_, j) => j !== i),
                                    )
                                  }
                                >
                                  Remover
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {can(perms, "contacts", "read") ? (
                  <SupplierPicker
                    exclude={suppliers.map((s) => s.supplierId)}
                    onPick={(c) => {
                      setNames((n) => ({ ...n, [c.id]: c.name }));
                      f.set("suppliers", [
                        ...suppliers,
                        { supplierId: c.id, isDefault: suppliers.length === 0 },
                      ]);
                    }}
                  />
                ) : (
                  <p className="text-muted small">
                    Para escolher fornecedores é preciso acesso a Clientes e Fornecedores.
                  </p>
                )}
              </div>
            ),
          },
          {
            key: "imagens",
            label: "Imagens",
            content: isNew ? (
              <p className="text-muted">Salve o produto para enviar imagens.</p>
            ) : (
              <>
                <QueryStatus loading={images.isLoading} error={images.error} />
                {images.isSuccess && (
                  <ImageGallery
                    items={images.data.data.map((i) => ({
                      id: i.id,
                      url: i.url,
                      external: i.external,
                      label: i.fileName ?? i.url,
                    }))}
                    canEdit={can(perms, "products", "update")}
                    busy={imagesBusy}
                    onUpload={(file) => upload.mutate(file)}
                    onAddUrl={(url) => addUrl.mutate(url)}
                    onReorder={(ids) => reorder.mutate(ids)}
                    onDelete={(imageId) => delImage.mutate(imageId)}
                    hint={`PNG, JPEG ou WebP até 10 MB, no máximo ${MAX_PRODUCT_IMAGES} imagens. A primeira é a principal.`}
                  />
                )}
              </>
            ),
          },
          {
            key: "custom",
            label: "Campos customizados",
            content: (
              <CustomFieldsPanel
                fields={fields.data?.data ?? []}
                values={v.customFields ?? {}}
                onChange={(cf) => f.set("customFields", cf)}
                errors={err}
              />
            ),
          },
        ]}
      />
      {!canEdit && <p className="text-muted small mt-2">Você só tem permissão de leitura neste cadastro.</p>}
    </FormPage>
  );
}
